"use client";

import { useEffect } from "react";
import { composeTitle, getSubcategoryName } from "@/lib/listing/titleComposer";
import { SparklesIcon } from "@heroicons/react/24/outline";
import Input from "@/components/ui/Input";
import { getCategoryFieldConfig } from "@/lib/listing/fieldConfigs";
import { ItemCategory } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  emptyFamilySpecs,
  getFamilyRuntime,
  type ListingFamilySpecs,
} from "@/lib/listing/listingFamilyRuntime";
import {
  listingSpecPluginHidesBrandLine,
  listingSpecPluginHidesModelLine,
} from "@/lib/listing/listingSpecPlugin";

interface EnhancedTitleInputProps {
  brand?: string;
  size?: string;
  model?: string;
  subcategoryId?: string;
  category?: ItemCategory | string | null;
  familySpecs?: ListingFamilySpecs;
  title: string;
  onTitleChange: (title: string) => void;
  error?: string;
  className?: string;
  placeholder?: string;
}

export default function EnhancedTitleInput({
  brand,
  size,
  model,
  subcategoryId,
  category,
  familySpecs,
  title,
  onTitleChange,
  error,
  className = "",
  placeholder = "What should we call this item?",
}: EnhancedTitleInputProps) {
  const subcategoryName = getSubcategoryName(subcategoryId);
  const config = getCategoryFieldConfig(category);
  const familyRuntime = getFamilyRuntime(subcategoryId);
  const isStructuredTitleScoped = familyRuntime != null;

  const composedTitle = composeTitle({
    size,
    brand,
    model,
    subcategoryId: subcategoryId || undefined,
    subcategoryName: subcategoryName || undefined,
    familySpecs: familySpecs ?? emptyFamilySpecs(),
  });

  useEffect(() => {
    const next = composedTitle ?? "";
    if (next !== title) {
      onTitleChange(next);
    }
  }, [composedTitle, title, onTitleChange]);

  const hasBrand = !!brand?.trim();
  const hasSize = !!size?.trim();
  const hasModel = !!model?.trim();
  const hasSubcategory = !!subcategoryId;

  const getHelperMessage = () => {
    if (composedTitle.trim()) {
      return "This title cannot be edited.";
    }
    if (isStructuredTitleScoped) {
      if (subcategoryId === "office-table") {
        return "Your title uses “Office Table” — add optional desk details anytime.";
      }
      return "Add the required details in the previous step to generate your title.";
    }
    if (hasSubcategory && config) {
      const suggestions: string[] = [];
      if (
        config.brand &&
        !hasBrand &&
        !listingSpecPluginHidesBrandLine(subcategoryId)
      ) {
        suggestions.push("brand");
      }
      if (config.size.enabled && !hasSize) suggestions.push("size/capacity");
      if (
        config.model &&
        !hasModel &&
        !listingSpecPluginHidesModelLine(subcategoryId)
      ) {
        suggestions.push("model");
      }
      if (suggestions.length > 0) {
        return `Add ${suggestions.join(", ")} below to generate your title.`;
      }
    }
    if (hasSubcategory) {
      return "Your title will appear here once there is enough detail to build it.";
    }
    return "Choose a category and subcategory first; your title will be generated automatically.";
  };

  const showQualityIndicators =
    hasSubcategory && config && !isStructuredTitleScoped;
  const showAutoBadge = composedTitle.trim().length > 0;

  return (
    <div className={className}>
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              name="title"
              value={title}
              onChange={() => {}}
              readOnly
              error={error}
              required
              placeholder={placeholder}
              className={cn("w-full", "bg-secondary cursor-default")}
              maxLength={50}
            />
          </div>
          {showAutoBadge && (
            <div
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg mt-0.5"
              title="Title is generated from your listing details"
            >
              <SparklesIcon className="h-4 w-4 text-primary-600 flex-shrink-0" />
              <span className="text-xs font-medium text-primary-700 hidden sm:inline">
                Auto
              </span>
            </div>
          )}
        </div>

        <p className="text-xs text-text-light leading-relaxed">
          {getHelperMessage()}
        </p>

        {showQualityIndicators && config && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-foreground">Title details:</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {hasBrand && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-md font-medium">
                  ✓ Brand
                </span>
              )}
              {hasSize && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-md font-medium">
                  ✓ Size
                </span>
              )}
              {hasModel && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-md font-medium">
                  ✓ Model
                </span>
              )}
              {config.brand && !hasBrand && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-text-light rounded-md">
                  + Brand
                </span>
              )}
              {config.size.enabled && !hasSize && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-text-light rounded-md">
                  + {config.size.label}
                </span>
              )}
              {config.model && !hasModel && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-text-light rounded-md">
                  + Model
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
